import axios from '../utils/index';
import { Pat, HttpResponse } from '@/typings/request';
const taskerApi = {
    async getTaskerList(params: Pat) {
        return axios.get(`/tasker/list`, { params });
    },
    async changeTaskerStatus(data: Pat) {
        return axios.put(`/tasker/disable/${data.taskerId}?isDisable=${data.isDisable}`, data);
    },
    async deleteTasker(data: Pat) {
        return axios.delete(`/tasker/delete/${data.taskerId}`, data);
    },
    /** Replaces the full set of market categories assigned to a tasker (empty array clears). Ids as strings — Snowflake ids lose precision as JS numbers. */
    async updateTaskerMarketCategories(taskerId: string, categoryIds: (string | number)[]) {
        return axios.put(`/tasker/${taskerId}/market-categories`, { categoryIds: categoryIds.map(String) });
    },
    /** Replaces the full set of B2B categories assigned to a tasker (empty array clears). Ids as strings — Snowflake ids lose precision as JS numbers. */
    async updateTaskerB2bCategories(taskerId: string, categoryIds: (string | number)[]) {
        return axios.put(`/tasker/${taskerId}/b2b-categories`, { categoryIds: categoryIds.map(String) });
    },
    /** Sets or clears admin tasker category (tb_tasker.tasker_category_id). Send id as string — JSON numbers lose precision for Snowflake ids in JS. */
    async updateTaskerTaskerCategory(taskerId: string, taskerCategoryId: string | number | null) {
        const payload =
            taskerCategoryId === null || taskerCategoryId === undefined || taskerCategoryId === ''
                ? { taskerCategoryId: null }
                : { taskerCategoryId: String(taskerCategoryId) };
        return axios.put(`/tasker/${taskerId}/tasker-category`, payload);
    },
    async getTaskerInfo(data: Pat) {
        return axios.get(`/tasker/detail/${data.taskerId}`, data);
    },
    /** Admin edits a tasker's profile. taskerId as string — Snowflake ids lose precision as JS numbers. */
    async updateTaskerInfo(taskerId: string, data: Pat) {
        return axios.put(`/tasker/update/${taskerId}`, data);
    },
    /** Admin edits a review (rating score + comment). ratingId as string (Snowflake id). */
    async updateReview(ratingId: string, data: Pat) {
        return axios.put(`/tasker/review/${ratingId}`, data);
    },
    /** Send an OTP to a new email so the admin can verify it before saving a tasker email change. */
    async sendEmailOtp(email: string) {
        return axios.post(`/tasker/send-email-otp`, null, { params: { email } });
    },
    /** Send an OTP to a new mobile so the admin can verify it before saving a tasker mobile change. */
    async sendMobileOtp(areaCode: string, mobile: string) {
        return axios.post(`/tasker/send-mobile-otp`, null, { params: { areaCode, mobile } });
    },
    /** Verify (without saving) the OTP for a new email; rejects if the code is wrong/expired. */
    async verifyEmailOtp(email: string, code: string) {
        return axios.post(`/tasker/verify-email-otp`, null, { params: { email, code } });
    },
    /** Verify (without saving) the OTP for a new mobile; rejects if the code is wrong/expired. */
    async verifyMobileOtp(areaCode: string, mobile: string, code: string) {
        return axios.post(`/tasker/verify-mobile-otp`, null, { params: { areaCode, mobile, code } });
    },
    async getTaskerGallery(taskerId: string) {
        return axios.get(`/tasker/gallery/${taskerId}`);
    },
    async getTaskerTaskList(data: Pat) {
        return axios.get(
            `/task/tasker/list/${data.taskerId}?pageNum=${data.pageNum}&pageSize=${data.pageSize}&taskState=${data.taskState}&taskerId=${data.taskerId}`,
            { data }
        );
    },
    async getTaskerTaskDetail(data: Pat) {
        return axios.get(`/task/tasker/detail/${data.taskerId}?taskId=${data.taskId}&taskerId=${data.taskerId}`, {
            data,
        });
    },
    async getCustomerRatingList(data: Pat) {
        return axios.get(
            `/core/rating/list?ratingType=${data.ratingType}&pageNum=${data.pageNum}&pageSize=${data.pageSize}&targetId=${data.targetId}`,
            {
                data,
            }
        );
    },
    async getTaskerPaymentrecordList(data: Pat) {
        return axios.get(
            `/payment-record/tasker/list/${data.taskerId}?taskState=${data.taskState}&pageNum=${data.pageNum}&pageSize=${data.pageSize}&taskerId=${data.taskerId}`,
            {
                data,
            }
        );
    },
    async getTaskerCalendarList(taskerId: string) {
        return axios.get(`/tasker/calendar/list/${taskerId}`, {
            headers: {
                'Content-Type': 'application/x-www-form-urlencoded',
            },
        });
    },
    async verifyTasker(data: Pat) {
        return axios.post(`/tasker/verify-tasker-account`, data);
    },
    async deleteTaskerAccount(data: Pat) {
        return axios.delete(`/tasker/tasker-account`, { data });
    },
    async getTaskerApprovalList(params: Pat) {
        return axios.get(`/tasker/approval/list`, { params });
    },
    async updateTaskerApproval(taskerId: string, approvalStatus: 'approved' | 'rejected', rejectionReason?: string) {
        const payload: Pat = { approvalStatus };
        if (approvalStatus === 'rejected') {
            payload.rejectionReason = rejectionReason?.trim();
        }
        return axios.put(`/tasker/approval/${taskerId}`, payload);
    },
    /** Emails an approved website applicant a fresh password set-up link (earlier links stop working). */
    async resendSetupInvite(taskerId: string) {
        return axios.post(`/tasker/${taskerId}/resend-setup-invite`);
    },
    /** Saves the admin's edits to the tasker's website application ("Information"). */
    async updateTaskerApplication(taskerId: string, data: Pat) {
        return axios.put(`/tasker/${taskerId}/application`, data);
    },
};

export default taskerApi;
